import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Menu,
  Stack,
  Button,
  Switch,
  Divider,
  Tooltip,
  Container,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  MenuItem,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// ---------------------------------------------------------------------------
// Section model. The public /about-us page (kayfa-nextjs AboutDynamic.tsx)
// renders each section by `type`; the `data` shape per type is described by the
// schemas below. Array order === display order (persisted as `order` on save).
// ---------------------------------------------------------------------------

type FieldType = 'text' | 'textarea' | 'image' | 'url';
type Field = { key: string; label: string; type: FieldType; rtl?: boolean; help?: string };
type ListSchema = { key: string; itemLabel: string; addLabel: string; fields: Field[] };
type SectionSchema = {
  type: string;
  label: string;
  desc: string;
  icon: string;
  fields: Field[];
  list?: ListSchema;
};

type Section = {
  type: string;
  enabled?: boolean;
  order?: number;
  data?: Record<string, any>;
};

const EN = (k: string, l: string): Field => ({ key: k, label: l, type: 'text' });
const AR = (k: string, l: string): Field => ({ key: k, label: l, type: 'text', rtl: true });
const TA = (k: string, l: string): Field => ({ key: k, label: l, type: 'textarea' });
const TA_AR = (k: string, l: string): Field => ({ key: k, label: l, type: 'textarea', rtl: true });

const SCHEMAS: SectionSchema[] = [
  {
    type: 'hero',
    label: 'Hero banner',
    desc: 'Top banner with headline, subtitle, background image and a call-to-action button.',
    icon: 'eva:image-outline',
    fields: [
      EN('title', 'Title (EN)'),
      AR('title_ar', 'Title (AR)'),
      TA('subtitle', 'Subtitle (EN)'),
      TA_AR('subtitle_ar', 'Subtitle (AR)'),
      { key: 'image', label: 'Background image', type: 'image' },
      EN('ctaText', 'Button text (EN)'),
      AR('ctaText_ar', 'Button text (AR)'),
      { key: 'ctaLink', label: 'Button link', type: 'url' },
    ],
  },
  {
    type: 'mission',
    label: 'Mission',
    desc: 'Heading + paragraph with an optional side image.',
    icon: 'eva:flag-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)'), TA('body', 'Body (EN)'), TA_AR('body_ar', 'Body (AR)'), { key: 'image', label: 'Image', type: 'image' }],
  },
  {
    type: 'vision',
    label: 'Vision',
    desc: 'Heading + paragraph with an optional side image.',
    icon: 'eva:eye-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)'), TA('body', 'Body (EN)'), TA_AR('body_ar', 'Body (AR)'), { key: 'image', label: 'Image', type: 'image' }],
  },
  {
    type: 'coreValues',
    label: 'Core Values',
    desc: 'A grid of value cards, each with an emoji/icon, title and description.',
    icon: 'eva:award-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Value',
      addLabel: 'Add value',
      fields: [{ key: 'icon', label: 'Emoji / icon', type: 'text', help: 'e.g. 🎯' }, EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), TA('text', 'Text (EN)'), TA_AR('text_ar', 'Text (AR)')],
    },
  },
  {
    type: 'learningModel',
    label: 'Learning Model',
    desc: 'A numbered list of steps describing how learning works.',
    icon: 'eva:list-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'steps',
      itemLabel: 'Step',
      addLabel: 'Add step',
      fields: [EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), TA('text', 'Text (EN)'), TA_AR('text_ar', 'Text (AR)')],
    },
  },
  {
    type: 'partners',
    label: 'Partners / Sponsors',
    desc: 'A row of partner or sponsor logos, each optionally linking out.',
    icon: 'eva:briefcase-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Partner',
      addLabel: 'Add partner',
      fields: [EN('name', 'Name'), { key: 'logo', label: 'Logo', type: 'image' }, { key: 'link', label: 'Website link', type: 'url' }],
    },
  },
  {
    type: 'team',
    label: 'Team / Leadership',
    desc: 'Team member cards with photo, name, role, short bio and LinkedIn.',
    icon: 'eva:people-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'members',
      itemLabel: 'Member',
      addLabel: 'Add member',
      fields: [
        { key: 'photo', label: 'Photo', type: 'image' },
        EN('name', 'Name (EN)'),
        AR('name_ar', 'Name (AR)'),
        EN('role', 'Role (EN)'),
        AR('role_ar', 'Role (AR)'),
        TA('bio', 'Bio (EN)'),
        TA_AR('bio_ar', 'Bio (AR)'),
        { key: 'linkedin', label: 'LinkedIn URL', type: 'url' },
      ],
    },
  },
  {
    type: 'testimonials',
    label: 'Testimonials',
    desc: 'Quote cards with the author’s photo, name and role.',
    icon: 'eva:message-square-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Testimonial',
      addLabel: 'Add testimonial',
      fields: [
        { key: 'photo', label: 'Photo', type: 'image' },
        EN('name', 'Name (EN)'),
        AR('name_ar', 'Name (AR)'),
        EN('role', 'Role (EN)'),
        AR('role_ar', 'Role (AR)'),
        TA('quote', 'Quote (EN)'),
        TA_AR('quote_ar', 'Quote (AR)'),
      ],
    },
  },
  {
    type: 'accreditations',
    label: 'Accreditations / Awards',
    desc: 'Badges/logos of accreditations or awards, each optionally linking out.',
    icon: 'eva:shield-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Item',
      addLabel: 'Add item',
      fields: [{ key: 'image', label: 'Badge / logo', type: 'image' }, EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), { key: 'link', label: 'Link', type: 'url' }],
    },
  },
  {
    type: 'milestones',
    label: 'Journey / Milestones',
    desc: 'A timeline of milestones, each with a year, title and description.',
    icon: 'eva:calendar-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Milestone',
      addLabel: 'Add milestone',
      fields: [{ key: 'year', label: 'Year', type: 'text', help: 'e.g. 2019' }, EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), TA('text', 'Text (EN)'), TA_AR('text_ar', 'Text (AR)')],
    },
  },
];

const schemaFor = (type: string) => SCHEMAS.find((s) => s.type === type);

// ---------------------------------------------------------------------------
// Small reusable image field: uploads to /files/media, stores the returned URL.
// ---------------------------------------------------------------------------
function ImageField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (url: string) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
      const url = res.data?.url ?? res.data?.data?.url;
      if (!url) throw new Error('No URL returned');
      onChange(url);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Upload failed', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {value ? (
          <Box
            component="img"
            src={value}
            alt=""
            sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }}
          />
        ) : (
          <Box sx={{ width: 56, height: 56, borderRadius: 1, bgcolor: 'background.neutral', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Iconify icon="eva:image-outline" sx={{ color: 'text.disabled' }} />
          </Box>
        )}
        <Button component="label" size="small" variant="outlined" disabled={busy} startIcon={busy ? <CircularProgress size={14} /> : <Iconify icon="eva:cloud-upload-outline" />}>
          {value ? 'Replace' : 'Upload'}
          <input hidden type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
        </Button>
        {value ? (
          <IconButton size="small" color="error" onClick={() => onChange('')}>
            <Iconify icon="eva:close-outline" />
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
}

// Render one field (text/textarea/url/image) bound to an object + key.
function FieldInput({
  field,
  obj,
  onChange,
}: {
  field: Field;
  obj: Record<string, any>;
  onChange: (key: string, value: any) => void;
}) {
  if (field.type === 'image') {
    return <ImageField label={field.label} value={obj[field.key] || ''} onChange={(url) => onChange(field.key, url)} />;
  }
  return (
    <TextField
      label={field.label}
      value={obj[field.key] || ''}
      onChange={(e) => onChange(field.key, e.target.value)}
      fullWidth
      size="small"
      multiline={field.type === 'textarea'}
      minRows={field.type === 'textarea' ? 2 : undefined}
      helperText={field.help}
      inputProps={field.rtl ? { dir: 'rtl' } : undefined}
    />
  );
}

// ---------------------------------------------------------------------------

export default function AboutPageEditor() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.ABOUT_PAGE.GET);
      const data = res.data?.data ?? res.data;
      const list: Section[] = Array.isArray(data?.sections) ? data.sections : [];
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSections(list);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load About page', { variant: 'error' });
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const setSection = (idx: number, patch: Partial<Section>) =>
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const setData = (idx: number, key: string, value: any) =>
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, data: { ...(s.data || {}), [key]: value } } : s)));

  const setListItem = (idx: number, listKey: string, itemIdx: number, key: string, value: any) =>
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        const arr = [...((s.data?.[listKey] as any[]) || [])];
        arr[itemIdx] = { ...arr[itemIdx], [key]: value };
        return { ...s, data: { ...(s.data || {}), [listKey]: arr } };
      }),
    );

  const addListItem = (idx: number, listKey: string) =>
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        const arr = [...((s.data?.[listKey] as any[]) || []), {}];
        return { ...s, data: { ...(s.data || {}), [listKey]: arr } };
      }),
    );

  const removeListItem = (idx: number, listKey: string, itemIdx: number) =>
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        const arr = ((s.data?.[listKey] as any[]) || []).filter((_, j) => j !== itemIdx);
        return { ...s, data: { ...(s.data || {}), [listKey]: arr } };
      }),
    );

  const move = (idx: number, dir: -1 | 1) =>
    setSections((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const removeSection = (idx: number) => setSections((prev) => prev.filter((_, i) => i !== idx));

  const addSection = (type: string) => {
    setAddAnchor(null);
    setSections((prev) => [...prev, { type, enabled: true, data: {} }]);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      sections: sections.map((s, i) => ({
        type: s.type,
        enabled: s.enabled !== false,
        order: i,
        data: s.data || {},
      })),
    };
    try {
      await axiosInstance.patch(APIS.ABOUT_PAGE.UPDATE, payload);
      enqueueSnackbar('About page saved');
      await fetchData();
    } catch (e: any) {
      enqueueSnackbar(e?.messages?.[0] || e?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const usableTypes = useMemo(() => SCHEMAS, []);

  return (
    <Page title="About Page">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="About Page"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'About Page' }]}
          action={
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" startIcon={<Iconify icon="eva:plus-fill" />} onClick={(e) => setAddAnchor(e.currentTarget)}>
                Add section
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving || loading} startIcon={saving ? <CircularProgress size={16} /> : <Iconify icon="eva:save-outline" />}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </Stack>
          }
        />

        <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
          {usableTypes.map((s) => (
            <MenuItem key={s.type} onClick={() => addSection(s.type)}>
              <Iconify icon={s.icon} sx={{ mr: 1.5, width: 20, height: 20 }} />
              {s.label}
            </MenuItem>
          ))}
        </Menu>

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Build the public <b>About Us</b> page from these blocks. Drag order with the arrows, hide a block with its
            toggle, and remove any you don’t need. When there are <b>no</b> sections here, the site shows its original
            built-in About layout. The Impact Numbers band is managed under <b>Home Statistics</b> and always shows below.
          </Typography>
        </Card>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : sections.length === 0 ? (
          <Card sx={{ p: 6, textAlign: 'center' }}>
            <Iconify icon="eva:layout-outline" sx={{ width: 48, height: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6">No sections yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The site is showing its default About layout. Add sections to take over the page.
            </Typography>
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={(e) => setAddAnchor(e.currentTarget)}>
              Add your first section
            </Button>
          </Card>
        ) : (
          <Stack spacing={2}>
            {sections.map((section, idx) => {
              const schema = schemaFor(section.type);
              const enabled = section.enabled !== false;
              return (
                <Card key={idx} sx={{ p: 2.5, opacity: enabled ? 1 : 0.6 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Iconify icon={schema?.icon || 'eva:cube-outline'} sx={{ width: 24, height: 24, color: 'primary.main' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1">{schema?.label || section.type}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {schema?.desc || 'Unknown section type'}
                      </Typography>
                    </Box>
                    <Label color={enabled ? 'success' : 'default'}>{enabled ? 'Visible' : 'Hidden'}</Label>
                    <Tooltip title={enabled ? 'Hide from site' : 'Show on site'}>
                      <Switch checked={enabled} onChange={(e) => setSection(idx, { enabled: e.target.checked })} />
                    </Tooltip>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton size="small" disabled={idx === 0} onClick={() => move(idx, -1)}>
                          <Iconify icon="eva:arrow-upward-outline" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton size="small" disabled={idx === sections.length - 1} onClick={() => move(idx, 1)}>
                          <Iconify icon="eva:arrow-downward-outline" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove section">
                      <IconButton size="small" color="error" onClick={() => removeSection(idx)}>
                        <Iconify icon="eva:trash-2-outline" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {!schema ? (
                    <Typography variant="body2" color="error">
                      This section type (“{section.type}”) is not recognised by this editor version.
                    </Typography>
                  ) : (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      {/* top-level fields */}
                      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        {schema.fields.map((f) => (
                          <Box key={f.key} sx={{ gridColumn: f.type === 'textarea' ? '1 / -1' : undefined }}>
                            <FieldInput field={f} obj={section.data || {}} onChange={(k, v) => setData(idx, k, v)} />
                          </Box>
                        ))}
                      </Box>

                      {/* repeatable list */}
                      {schema.list ? (
                        <Box sx={{ mt: 2.5 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">{schema.list.itemLabel}s</Typography>
                            <Button size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => addListItem(idx, schema.list!.key)}>
                              {schema.list.addLabel}
                            </Button>
                          </Stack>
                          <Stack spacing={1.5}>
                            {((section.data?.[schema.list.key] as any[]) || []).map((item, itemIdx) => (
                              <Box key={itemIdx} sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', position: 'relative' }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {schema.list!.itemLabel} #{itemIdx + 1}
                                  </Typography>
                                  <IconButton size="small" color="error" onClick={() => removeListItem(idx, schema.list!.key, itemIdx)}>
                                    <Iconify icon="eva:close-outline" />
                                  </IconButton>
                                </Stack>
                                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                  {schema.list!.fields.map((f) => (
                                    <Box key={f.key} sx={{ gridColumn: f.type === 'textarea' ? '1 / -1' : undefined }}>
                                      <FieldInput
                                        field={f}
                                        obj={item || {}}
                                        onChange={(k, v) => setListItem(idx, schema.list!.key, itemIdx, k, v)}
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            ))}
                            {((section.data?.[schema.list.key] as any[]) || []).length === 0 ? (
                              <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                No {schema.list.itemLabel.toLowerCase()}s yet.
                              </Typography>
                            ) : null}
                          </Stack>
                        </Box>
                      ) : null}
                    </>
                  )}
                </Card>
              );
            })}
          </Stack>
        )}
      </Container>
    </Page>
  );
}
